import { Settings } from '@streamr/tv-engine';
import { EventEmitter } from 'eventemitter3';
/**
 * Tracks per-session audio level and speaking state from inbound
 * MediaPackets.  Computes a dominant speaker with hysteresis to
 * avoid rapid switching.
 */
export class RemoteSpeechTracker extends EventEmitter {
    entries = new Map();
    dominantSession = null;
    candidateSession = null;
    candidateSince;
    update(userSessionId, userId, audioLevel, isSpeaking) {
        const existing = this.entries.get(userSessionId);
        const changed = !existing || existing.isSpeaking !== isSpeaking;
        this.entries.set(userSessionId, {
            audioLevel,
            isSpeaking,
            lastUpdateTime: performance.now(),
            userId,
        });
        if (changed) {
            this.emit('remoteSpeechStateChanged', userSessionId, userId, isSpeaking, audioLevel);
        }
        this.evaluateDominant();
    }
    removeSession(userSessionId) {
        this.entries.delete(userSessionId);
        if (this.dominantSession === userSessionId) {
            this.dominantSession = null;
            this.candidateSession = null;
            this.candidateSince = undefined;
            this.evaluateDominant();
        }
        if (this.candidateSession === userSessionId) {
            this.candidateSession = null;
            this.candidateSince = undefined;
        }
    }
    getDominantSession() {
        return this.dominantSession;
    }
    getEntries() {
        return this.entries;
    }
    destroy() {
        this.entries.clear();
        this.dominantSession = null;
        this.candidateSession = null;
        this.candidateSince = undefined;
        this.removeAllListeners();
    }
    evaluateDominant() {
        let bestSession = null;
        let bestLevel = -1;
        for (const [session, entry] of this.entries) {
            if (entry.isSpeaking && entry.audioLevel > bestLevel) {
                bestLevel = entry.audioLevel;
                bestSession = session;
            }
        }
        if (bestSession === null) {
            if (this.dominantSession !== null) {
                this.dominantSession = null;
                this.candidateSession = null;
                this.candidateSince = undefined;
                this.emit('dominantSpeakerChanged', null, null);
            }
            return;
        }
        if (bestSession === this.dominantSession) {
            this.candidateSession = null;
            this.candidateSince = undefined;
            return;
        }
        const currentEntry = this.dominantSession
            ? this.entries.get(this.dominantSession)
            : undefined;
        const currentLevel = currentEntry?.isSpeaking
            ? (currentEntry.audioLevel ?? 0)
            : 0;
        if (bestLevel <= currentLevel + Settings.dominantSpeakerSwitchMargin) {
            this.candidateSession = null;
            this.candidateSince = undefined;
            return;
        }
        const now = performance.now();
        if (this.candidateSession !== bestSession) {
            this.candidateSession = bestSession;
            this.candidateSince = now;
            return;
        }
        if (this.candidateSince !== undefined &&
            now - this.candidateSince >=
                Settings.dominantSpeakerSwitchDebounceMs) {
            this.dominantSession = bestSession;
            this.candidateSession = null;
            this.candidateSince = undefined;
            const entry = this.entries.get(bestSession);
            if (entry) {
                this.emit('dominantSpeakerChanged', bestSession, entry.userId);
            }
        }
    }
}
